import {
  Col,
  PageHeader,
  Popover,
  Row,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { SettingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import OnTraccrButton from '../common/buttons/OnTraccrButton';
import { getCustomFields } from './state/timecards.actions';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';
import { safeExtractFromObject } from '../helpers/helpers';

const nameColumn = { label: 'Name', value: 'name' };
const dateColumn = { label: 'Date', value: 'day' };
const totalTimeColumn = { label: 'Total Time', value: 'totalTime' };
const summaryDateColumn = { label: 'Date', value: 'date' };
const violationColumn = { label: 'Violation', value: 'hasViolation' };
const rejectionColumn = { label: 'Rejection', value: 'hasRejection' };
const overallStartTimeColumn = { label: 'Overall Start Time', value: 'StartTime' };
const overallEndTimeColumn = { label: 'Overall End Time', value: 'EndTime' };
const startTimeColumn = { label: 'Regular Start Time', value: 'RegularStartTime' };
const endTimeColumn = { label: 'Regular End Time', value: 'RegularEndTime' };
const breakStartTimeColumn = { label: 'Break Start Time', value: 'BreakStartTime' };
const breakEndTimeColumn = { label: 'Break End Time', value: 'BreakEndTime' };
const otStartTimeColumn = { label: 'OT Start Time', value: 'OTStartTime' };
const otEndTimeColumn = { label: 'OT End Time', value: 'OTEndTime' };
const doubleOTStartTimeColumn = { label: 'DT OT Start Time', value: 'DoubleOTStartTime' };
const doubleOTEndTimeColumn = { label: 'DT OT End Time', value: 'DoubleOTEndTime' };
const durationColumn = { label: 'Duration', value: 'Duration' };
const otDurationColumn = { label: 'OT Duration', value: 'OTDuration' };
const dtOtDurationColumn = { label: 'DT OT Duration', value: 'DTOTDuration' };
const regularDurationColumn = { label: 'Regular Duration', value: 'RegularDuration' };
const breakDurationColumn = { label: 'Break Duration', value: 'BreakDuration' };
const ptoDurationColumn = { label: 'PTO Duration', value: 'PTODuration' };
const typeColumn = { label: 'Type', value: 'Type' };
const projectColumn = (t) => ({ label: t('Project'), value: 'Project' });
const costcodeColumn = { label: 'Cost Code', value: 'CostCode' };
const phaseColumn = { label: 'Phase', value: 'Phase' };
const unionColumn = { label: 'Union', value: 'Union' };
const localClassColumn = { label: 'Local Class', value: 'LocalClass' };
const workClassColumn = { label: 'Work Class', value: 'WorkClass' };
const notesColumn = { label: 'Notes', value: 'Notes' };
const statusColumn = { label: 'Status', value: 'Status' };
const approvedByColumn = { label: 'Approved By', value: 'ApprovedBy' };
const enteredViaColumn = { label: 'Entered Via', value: 'enteredVia' };
const cardColumn = { label: 'Card', value: 'card' };
const nightShiftColumn = { label: 'Night Shift', value: 'isNightShift' };

const dayEntryDefaultColumns = [
  dateColumn,
  totalTimeColumn,
  overallStartTimeColumn,
  overallEndTimeColumn,
  startTimeColumn,
  endTimeColumn,
  breakStartTimeColumn,
  breakEndTimeColumn,
  otStartTimeColumn,
  otEndTimeColumn,
  doubleOTStartTimeColumn,
  doubleOTEndTimeColumn,
  ptoDurationColumn,
  violationColumn,
  rejectionColumn,
];

const getTimeCardEntryDefaultColumns = (t, enableNightShiftPremium = false) => {
  const cols = [
    startTimeColumn,
    endTimeColumn,
    breakStartTimeColumn,
    breakEndTimeColumn,
    otStartTimeColumn,
    otEndTimeColumn,
    doubleOTStartTimeColumn,
    doubleOTEndTimeColumn,
    durationColumn,
    regularDurationColumn,
    breakDurationColumn,
    otDurationColumn,
    dtOtDurationColumn,
    ptoDurationColumn,
    typeColumn,
    projectColumn(t),
    costcodeColumn,
    phaseColumn,
    unionColumn,
    localClassColumn,
    workClassColumn,
    notesColumn,
    statusColumn,
    enteredViaColumn,
    approvedByColumn,
    cardColumn,
  ];
  if (enableNightShiftPremium) {
    cols.push(nightShiftColumn);
  }
  return cols;
};

const getTimeCardSummaryDefaultColumns = (t, enableNightShiftPremium = false) => {
  const cols = [
    nameColumn,
    summaryDateColumn,
    startTimeColumn,
    endTimeColumn,
    breakStartTimeColumn,
    breakEndTimeColumn,
    otStartTimeColumn,
    otEndTimeColumn,
    doubleOTStartTimeColumn,
    doubleOTEndTimeColumn,
    durationColumn,
    regularDurationColumn,
    breakDurationColumn,
    otDurationColumn,
    dtOtDurationColumn,
    ptoDurationColumn,
    typeColumn,
    projectColumn(t),
    phaseColumn,
    costcodeColumn,
    unionColumn,
    localClassColumn,
    workClassColumn,
    notesColumn,
    violationColumn,
    statusColumn,
    enteredViaColumn,
    approvedByColumn,
    cardColumn,
  ];
  if (enableNightShiftPremium) {
    cols.push(nightShiftColumn);
  }
  return cols;
};

const validFieldTypes = new Set([
  'yes-no',
  'dropdown',
  'text',
  'dateRange',
  'dateTime',
  'attribute',
  'staticText',
  'table',
  'gpsLocation',
]);

export default function TimeCardsColumnChanger({ isSummary }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const enableNightShiftPremium = settings?.enableNightShiftPremium;

  const timeCardEntryDefaultColumns = getTimeCardEntryDefaultColumns(t, enableNightShiftPremium);

  const timeCardSummaryDefaultColumns = getTimeCardSummaryDefaultColumns(t, enableNightShiftPremium);

  const [selectedSummaryColumns, setSelectedSummaryColumns] = useState(
    timeCardSummaryDefaultColumns.map((column) => column.value),
  );

  const [selectedDayEntryColumns, setSelectedDayEntryColumns] = useState(
    dayEntryDefaultColumns.map((column) => column.value),
  );
  const [selectedTimeCardEntryColumns, setSelectedTimeCardEntryColumns] = useState(
    timeCardEntryDefaultColumns.map((column) => column.value),
  );
  const [selectedCustomFieldColumns, setSelectedCustomFieldColumns] = useState();

  const customFields = useSelector((state) => state.timecards.customFields);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);

  useEffect(() => {
    dispatch(getCustomFields());
  }, []);

  useEffect(() => {
    const rawDayEntryColumns = window.localStorage.getItem('timeCardDayEntryColumns');
    const rawTimeCardEntryColumns = window.localStorage.getItem('timeCardEntryColumns');
    const rawCustomFieldColumns = window.localStorage.getItem('timeCardCustomFieldColumns');
    const rawSummaryColumns = window.localStorage.getItem('timeCardSummaryColumns');

    if (rawDayEntryColumns) {
      setSelectedDayEntryColumns(JSON.parse(rawDayEntryColumns));
    }

    if (rawTimeCardEntryColumns) {
      setSelectedTimeCardEntryColumns(JSON.parse(rawTimeCardEntryColumns));
    }

    if (rawCustomFieldColumns) {
      setSelectedCustomFieldColumns(JSON.parse(rawCustomFieldColumns));
    }

    if (rawSummaryColumns) {
      setSelectedSummaryColumns(JSON.parse(rawSummaryColumns));
    }
  }, []);

  const onColumnChangeCreator = ({
    localStorageKey,
    setState,
    existingValues = [],
  }) => (column) => (checked) => {
    const newChecked = [...(existingValues ?? [])];
    if (checked) {
      newChecked.push(column);
    } else {
      const index = newChecked.indexOf(column);
      if (index > -1) {
        newChecked.splice(index, 1);
      }
    }
    setState(newChecked);
    window.localStorage.setItem(localStorageKey, JSON.stringify(newChecked));
    window.dispatchEvent(new CustomEvent('localStorageSetItem', { detail: localStorageKey }));
  };

  const onDayEntryChange = onColumnChangeCreator({
    localStorageKey: 'timeCardDayEntryColumns',
    setState: setSelectedDayEntryColumns,
    existingValues: selectedDayEntryColumns,
  });

  const onTimeCardEntryChange = onColumnChangeCreator({
    localStorageKey: 'timeCardEntryColumns',
    setState: setSelectedTimeCardEntryColumns,
    existingValues: selectedTimeCardEntryColumns,
  });

  const onCustomColumnChange = onColumnChangeCreator({
    localStorageKey: 'timeCardCustomFieldColumns',
    setState: setSelectedCustomFieldColumns,
    existingValues: selectedCustomFieldColumns,
  });

  const onSummaryColumnChange = onColumnChangeCreator({
    localStorageKey: 'timeCardSummaryColumns',
    setState: setSelectedSummaryColumns,
    existingValues: selectedSummaryColumns,
  });

  const selectedCustomFieldColumnsSet = useMemo(() => (
    new Set(selectedCustomFieldColumns)
  ), [selectedCustomFieldColumns]);

  const customFieldColumns = useMemo(() => {
    const columns = [];
    Object.values(customFields).forEach((customField) => {
      customField?.fields?.forEach?.(({ hidden, fields }) => {
        if (hidden) return;
        fields?.forEach?.((subField) => {
          if (!selectedDivisions.has(customField?.divisionId)
            && !selectedCustomFieldColumnsSet.has(subField.id)) return;
          if (validFieldTypes.has(subField.selectedType)) {
            columns.push({
              label: subField.configProps.title,
              value: subField.id,
            });
          }
        });
      });
    });

    return columns;
  }, [customFields, selectedDivisions, selectedCustomFieldColumnsSet]);

  const selectedDayEntryColumnSet = useMemo(() => (
    new Set(selectedDayEntryColumns)
  ), [selectedDayEntryColumns]);

  const selectedTimeCardEntryColumnSet = useMemo(() => (
    new Set(selectedTimeCardEntryColumns)
  ), [selectedTimeCardEntryColumns]);

  const selectedSummaryColumnSet = useMemo(() => (
    new Set(selectedSummaryColumns)
  ), [selectedSummaryColumns]);

  const selectedCustomFieldColumnSet = useMemo(() => (
    new Set(selectedCustomFieldColumns)
  ), [selectedCustomFieldColumns]);

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={(
        <div style={{ width: 250, maxHeight: 500, overflowY: 'auto' }}>
          {!isSummary && (
            <>
              <Row>
                <PageHeader
                  title="Day Entry Columns"
                  style={{
                    padding: 0,
                    marginBottom: 5,
                    fontFamily: 'raleway-medium',
                  }}
                />
              </Row>
              {dayEntryDefaultColumns.map((column) => (
                <Row key={column.value} style={{ marginTop: 5, marginLeft: 5, width: '100%' }}>
                  <Col span={24}>
                    <OnTraccrCheckbox
                      onChange={onDayEntryChange(column.value)}
                      value={selectedDayEntryColumnSet.has(column.value)}
                      label={column.label}
                    />
                  </Col>
                </Row>
              ))}
              <br />
              <Row>
                <PageHeader
                  title="Time Card Entry Columns"
                  style={{
                    padding: 0,
                    marginLeft: 5,
                    marginBottom: 5,
                    fontFamily: 'raleway-medium',
                  }}
                />
              </Row>
              {timeCardEntryDefaultColumns.map((column) => (
                <Row key={column.value} style={{ marginTop: 5, marginLeft: 5, width: '100%' }}>
                  <Col span={24}>
                    <OnTraccrCheckbox
                      onChange={onTimeCardEntryChange(column.value)}
                      value={selectedTimeCardEntryColumnSet.has(column.value)}
                      label={column.label}
                    />
                  </Col>
                </Row>
              ))}
            </>
          )}
          {isSummary && (
            <>
              <Row>
                <PageHeader
                  title="Summary View Columns"
                  style={{
                    padding: 0,
                    marginLeft: 5,
                    marginBottom: 5,
                    fontFamily: 'raleway-medium',
                  }}
                />
              </Row>
              {timeCardSummaryDefaultColumns.map((column) => (
                <Row key={column.value} style={{ marginTop: 5, marginLeft: 5, width: '100%' }}>
                  <Col span={24}>
                    <OnTraccrCheckbox
                      onChange={onSummaryColumnChange(column.value)}
                      value={selectedSummaryColumnSet.has(column.value)}
                      label={column.label}
                    />
                  </Col>
                </Row>
              ))}
              <br />
            </>
          )}
          <Row>
            <PageHeader
              title="Custom Field Columns"
              style={{
                padding: 0,
                marginLeft: 5,
                marginBottom: 5,
                fontFamily: 'raleway-medium',
              }}
            />
            {customFieldColumns.map((column) => (
              <Row key={column.value} style={{ marginTop: 5, marginLeft: 5, width: '100%' }}>
                <Col span={24}>
                  <OnTraccrCheckbox
                    onChange={onCustomColumnChange(column.value)}
                    value={selectedCustomFieldColumnSet.has(column.value)}
                    label={column.label}
                  />
                </Col>
              </Row>
            ))}
          </Row>
        </div>
      )}
    >
      <OnTraccrButton
        title="Columns"
        icon={<SettingOutlined />}
        type="cancel"
      />
    </Popover>
  );
}

TimeCardsColumnChanger.propTypes = {
  isSummary: PropTypes.bool,
};

TimeCardsColumnChanger.defaultProps = {
  isSummary: false,
};
